// External libraries
import React, { useEffect, useState } from "react"
import moment from "moment"
import Dropzone from "react-dropzone"
import { useDispatch, useSelector } from "react-redux"

// Ant Design components
import { Button, DatePicker, Modal, notification, Select, Tooltip } from "antd"

// Project components and utilities
import { Icons } from "components"
import { setLoader } from "store/actions"
import { API, Endpoints } from "utils/api"
import Strings from "utils/strings"
import { humanFileSize, translateImportError } from "utils/utils"

const ImportModal: React.FC<{
	showImportModal: string
	setShowImportModal: (arg: string) => void
	business?: any
	contract?: any
	getOrders: () => void
}> = ({
	showImportModal,
	setShowImportModal,
	business = null,
	contract = null,
	getOrders,
}) => {
	const mobile = useSelector((state: any) => state.mobile)

	const [showResponse, setShowResponse] = useState<any>(null) // Holds success and failed orders to show in the modal
	const [file, setFile] = useState<any>(null) // Holds the file to be uploaded
	const [uploadDate, setUploadDate] = useState<any>(moment.utc().startOf("day"))
	const [selectedShift, setSelectedShift] = useState<any>(
		contract?.defaultShift || null
	) // Holds the selected shift

	useEffect(() => {
		setSelectedShift(contract?.defaultShift || null) // Reset selected shift when contract changes
	}, [showImportModal, contract])

	// Handle onClose event for the modal
	const handleClose = () => {
		setShowImportModal("")
		setShowResponse(null)
		setFile(null)
		setUploadDate(moment.utc().startOf("day"))
	}

	const handleShowResponse = ({
		successCount,
		updatedObjects,
		failedObjects,
	}: {
		successCount: number
		updatedObjects: number
		failedObjects: any[]
	}) => {
		setShowResponse({
			successCount,
			updatedObjects,
			failedObjects,
		})
		getOrders()
	}

	const title =
		showImportModal === "orders"
			? Strings.orders.upload
			: Strings.orders.uploadBoxes
	return (
		<Modal
			centered={true}
			closable={true}
			onCancel={handleClose}
			visible={!!showImportModal}
			footer={null}
			bodyStyle={{ padding: 0 }}
			width={mobile ? "100%" : 600}
		>
			<div className="UploadModal">
				<div className="UploadTitle">
					<p>{`${title}. ${business?.name}, ${contract?.name}`}</p>
				</div>
				{showResponse ? (
					<ImportModalResponse
						successCount={showResponse?.successCount || 0}
						updatedObjects={showResponse?.updatedObjects || 0}
						failedObjects={showResponse?.failedObjects || []}
						handleClose={handleClose}
						type={showImportModal}
					/>
				) : (
					<>
						{showImportModal === "orders" && (
							<ImportOrdersContent
								uploadDate={uploadDate}
								setUploadDate={setUploadDate}
								selectedShift={selectedShift}
								setSelectedShift={setSelectedShift}
								file={file}
								setFile={setFile}
								shifts={contract?.shifts || []}
								contractId={contract?._id}
								businessId={business?._id}
								handleShowResponse={handleShowResponse}
							/>
						)}
						{showImportModal === "boxes" && (
							<ImportBoxesContent
								file={file}
								setFile={setFile}
								contractId={contract?._id}
								businessId={business?._id}
								handleShowResponse={handleShowResponse}
							/>
						)}
					</>
				)}
			</div>
		</Modal>
	)
}

const ImportOrdersContent: React.FC<{
	uploadDate: any
	setUploadDate: (date: any) => void
	selectedShift: any
	setSelectedShift: (shift: any) => void
	file: any
	setFile: (file: any) => void
	shifts?: any[]
	contractId: string
	businessId: string
	handleShowResponse: (response: any) => void
}> = ({
	uploadDate,
	setUploadDate,
	selectedShift,
	setSelectedShift,
	file,
	setFile,
	shifts = [],
	contractId,
	businessId,
	handleShowResponse,
}) => {
	const dispatch = useDispatch()

	const fileSize = file ? humanFileSize(file.size, true) : "0 B"
	return (
		<>
			<div className="UploadBody">
				<div className="shiftInfo">
					<div className="InlineGroup">
						<label
							htmlFor="upload_modal_date"
							className="InputLabel --label-required"
						>
							{Strings.fields.date + ":"}
						</label>
						<DatePicker
							value={uploadDate}
							onChange={(localValue: any) => {
								// TODO Do we need batchDate when it is not used for anything?
								setUploadDate(localValue)
							}}
							format={"DD/MM/YYYY"}
							style={{ width: "150px" }}
						/>
					</div>
					<div className="InlineGroup">
						<label
							htmlFor="upload_modal_shift"
							className="InputLabel --label-required"
						>
							{Strings.fields.shift + ":"}
						</label>
						<Select
							//className="shift_select"
							style={{ width: "150px" }}
							placeholder={Strings.fields.shift}
							//showSearch
							showArrow={true}
							disabled={false}
							filterOption={(input: any, option: any) =>
								option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							value={selectedShift}
							id="drawer_packaging_ype"
							onChange={(elem: any) => {
								setSelectedShift(elem)
							}}
						>
							{shifts.map((elem: any) => {
								return (
									<Select.Option value={elem._id}>{elem.name}</Select.Option>
								)
							})}
						</Select>
					</div>
				</div>
				<Dropzone
					className="UploadDropzone"
					id="upload_file"
					multiple={false}
					accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json"
					onDrop={(files: any) => setFile(files[0])}
				>
					{file ? (
						<React.Fragment>
							<p>Name: {file?.name}</p>
							<p>Size: {fileSize}</p>
						</React.Fragment>
					) : (
						<React.Fragment>
							<Icons.UploadFile />
							<p>{Strings.orders.uploadFile}</p>
						</React.Fragment>
					)}
				</Dropzone>
			</div>
			<div className="UploadFooter" style={{ justifyContent: "space-between" }}>
				<Tooltip title={Strings.orders.download}>
					<Button
						type="text"
						size="middle"
						icon={<Icons.Download />}
						onClick={() => {
							downloadTemplate("Orders Template", "Orders", [
								"ClientId*",
								"ClientName*",
								"ClientBusinessName",
								"Address*",
								"PostalCode*",
								"AddressNotes",
								"OrderDate*",
								"DeliveryDate*",
								"From*",
								"To*",
								"Phone",
								"Email",
								"OrderId*",
								"ServiceType",
								"PackageWidth",
								"PackageLength",
								"PackageHeight",
								"PackageWeight",
								"PackageVolume",
								"PackageItemQuantity",
								"Lot",
								"Latitude",
								"Longitude",
								"FormattedAddress",
								"City",
								"OnlyVan",
								"OnlyBike",
								"DistanceToDepot",
							])
						}}
					/>
				</Tooltip>
				<Button
					type="primary"
					size="large"
					onClick={() => {
						fileImportOrders(
							dispatch,
							businessId,
							contractId,
							uploadDate,
							selectedShift,
							file,
							handleShowResponse
						)
					}}
					disabled={
						!file || !uploadDate || !selectedShift || !businessId || !contractId
					}
				>
					{Strings.orders.import}
				</Button>
			</div>
		</>
	)
}

const ImportBoxesContent: React.FC<{
	file: any
	setFile: (file: any) => void
	contractId: string
	businessId: string
	handleShowResponse: (response: any) => void
}> = ({ file, setFile, contractId, businessId, handleShowResponse }) => {
	const dispatch = useDispatch()

	const fileSize = file ? humanFileSize(file.size, true) : "0 B"
	return (
		<>
			<div className="UploadBody">
				<Dropzone
					className="UploadDropzone"
					id="upload_file"
					multiple={false}
					accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json"
					onDrop={(files: any) => setFile(files[0])}
				>
					{file ? (
						<React.Fragment>
							<p>Name: {file?.name}</p>
							<p>Size: {fileSize}</p>
						</React.Fragment>
					) : (
						<React.Fragment>
							<Icons.UploadFile />
							<p>{Strings.orders.uploadFile}</p>
						</React.Fragment>
					)}
				</Dropzone>
			</div>
			<div className="UploadFooter" style={{ justifyContent: "space-between" }}>
				<Tooltip title={Strings.orders.download}>
					<Button
						type="text"
						size="middle"
						icon={<Icons.Download />}
						onClick={() => {
							downloadTemplate("Boxes Template", "Boxes", ["orderId", "boxes"])
						}}
					/>
				</Tooltip>
				<Button
					type="primary"
					size="large"
					onClick={() => {
						fileImportBoxes(
							dispatch,
							businessId,
							contractId,
							file,
							handleShowResponse
						)
					}}
					disabled={!file || !businessId || !contractId}
				>
					{Strings.orders.import}
				</Button>
			</div>
		</>
	)
}

const ImportModalResponse: React.FC<{
	successCount: number
	updatedObjects: any[]
	failedObjects: any[]
	handleClose: () => void
	type: string
}> = ({ successCount, updatedObjects, failedObjects, handleClose, type }) => {
	// Group failed orders by error message
	const groupedByError: { [error: string]: any[] } = failedObjects.reduce(
		(acc: any, order: any) => {
			const err = order.error || "unknown"
			if (!acc[err]) acc[err] = []
			acc[err].push(order)
			return acc
		},
		{}
	)

	const typeText = `${
		type === "orders"
			? Strings.orders.order.toLowerCase()
			: Strings.orders.box.toLowerCase()
	}s`

	const updateCount = updatedObjects.length || 0

	return (
		<>
			<div className="UploadBody">
				<div className="import-summary">
					<h2>Importação Concluída</h2>

					<div
						className="summary-items"
						style={{
							display: "flex",
							gap: "20px",
							justifyContent: "center",
							marginBottom: "20px",
						}}
					>
						<div
							className="summary-item success"
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								gap: "4px",
							}}
						>
							<svg
								className="icon"
								style={{ width: "20px", height: "20px", fill: "#52c41a" }}
							>
								<path d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 6.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z" />
							</svg>
							<span className="label" style={{ fontWeight: 500 }}>
								Sucesso:
							</span>
							<span
								className="count"
								style={{ fontSize: "0.9em", color: "#666" }}
							>
								{successCount} {typeText}
							</span>
						</div>
						{/* updateCount > 0 && */}
						{type === "orders" && (
							<div
								className="summary-item update"
								style={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									gap: "4px",
								}}
							>
								<svg
									className="icon"
									style={{
										width: "20px",
										height: "20px",
										fill: "#1890ff",
									}}
									viewBox="0 0 20 20"
								>
									<path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
								</svg>
								<span className="label" style={{ fontWeight: 500 }}>
									Atualizado:
								</span>
								<span
									className="count"
									style={{ fontSize: "0.9em", color: "#666" }}
								>
									{updateCount} {typeText}
								</span>
							</div>
						)}

						<div
							className="summary-item failure"
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								gap: "4px",
							}}
						>
							<svg
								className="icon"
								style={{ width: "20px", height: "20px", fill: "#ff4d4f" }}
							>
								<path
									fillRule="evenodd"
									d="M10 18a8 8 0 100-16 8 8 0 000 16zm2.707-9.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 001.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293z"
									clipRule="evenodd"
								/>
							</svg>
							<span className="label" style={{ fontWeight: 500 }}>
								Falha:
							</span>
							<span
								className="count"
								style={{ fontSize: "0.9em", color: "#666" }}
							>
								{failedObjects.length} {typeText}
							</span>
						</div>
					</div>

					{Object.entries(groupedByError).length > 0 && (
						<div className="error-details">
							<h5>Detalhes das Falhas:</h5>
							<ul>
								{Object.entries(groupedByError).map(([error, orders]) => (
									<li key={error}>
										<span>{translateImportError(error)}</span>
										<ul className="error-details-list">
											{orders.map((order: any, index: number) => {
												return (
													<li key={index}>
														<div>
															{order.orderId ? (
																<>
																	{order.orderId}
																	{error === "missing_fields" &&
																	order.missingField ? (
																		<> - {order.missingField}</>
																	) : null}
																</>
															) : (
																Strings.orders.missingOrderId
															)}
															{error === "order_id" &&
																order.deliveryDate &&
																order.status && (
																	<div
																		style={{
																			fontSize: "0.85em",
																			color: "#666",
																			marginTop: "2px",
																		}}
																	>
																		<div>
																			Data:{" "}
																			{moment(order.deliveryDate).format(
																				"DD/MM/YYYY"
																			)}
																		</div>
																		<div>Status: {order.status}</div>
																	</div>
																)}
														</div>
													</li>
												)
											})}
										</ul>
									</li>
								))}
							</ul>
						</div>
					)}

					{type === "orders" && updatedObjects.length > 0 && (
						<div className="error-details">
							<h5>Detalhes das Atualizações:</h5>
							<ul>
								<li>
									<span>Encomendas atualizadas:</span>
									<ul className="error-details-list">
										{updatedObjects.map((order: any, index: number) => (
											<li key={index}>
												<div>
													{order.orderId || "ID não disponível"}
													{((order.originalDate && order.newDate) ||
														order.originalStatus !== order.newStatus) && (
														<div
															style={{
																fontSize: "0.85em",
																color: "#666",
																marginTop: "2px",
															}}
														>
															{order.originalDate && order.newDate && (
																<div>
																	Data:{" "}
																	{moment(order.originalDate).format(
																		"DD/MM/YYYY"
																	)}{" "}
																	→ {moment(order.newDate).format("DD/MM/YYYY")}
																</div>
															)}
															{order.originalStatus && (
																<div>
																	Status: {order.originalStatus}{" "}
																	{order.newStatus !== order.originalStatus && (
																		<> → {order.newStatus}</>
																	)}
																</div>
															)}
															{order.originalLot &&
																order.newLot &&
																order.originalLot !== order.newLot && (
																	<div>
																		Lote: {order.originalLot} → {order.newLot}
																	</div>
																)}
														</div>
													)}
												</div>
											</li>
										))}
									</ul>
								</li>
							</ul>
						</div>
					)}
				</div>
			</div>
			<div className="UploadFooter" style={{ justifyContent: "flex-end" }}>
				<Button type="primary" onClick={handleClose} size="large">
					{Strings.generic.close}
				</Button>
			</div>
		</>
	)
}

async function fileImportOrders(
	dispatch: any,
	business: any,
	contract: any,
	date: any,
	shift: any,
	file: any,
	handleShowResponse: any = () => {}
) {
	const data = new FormData()
	data.append("file", file)
	data.append("business", business)
	data.append("contract", contract)
	data.append("shift", shift)
	data.append("date", moment(date).format("YYYY-MM-DD"))

	dispatch(setLoader(true))

	let response: any
	try {
		response = await API.post({
			url: Endpoints.uriOrders("import/fromFile"),
			data,
			axiosConfig: {
				timeout: 0,
			},
		})

		if (response.ok) {
			handleShowResponse({
				successCount: response.data.results?.successCount || 0,
				updatedObjects: response.data.results?.updatedOrders || 0,
				failedObjects: response.data.results?.failedOrders || [],
			})
		} else {
			notification.error({
				message: Strings.sidebar.orders,
				description: response?.data?.message || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}
	} catch (err) {
		notification.error({
			message: Strings.sidebar.orders,
			description: response?.data?.message || Strings.serverErrors.wentWrong,
			placement: "bottomRight",
			duration: 5,
		})
	} finally {
		dispatch(setLoader(false))
	}
}

async function fileImportBoxes(
	dispatch: any,
	business: any,
	contract: any,
	file: any,
	handleShowResponse: any = () => {}
) {
	const data = new FormData()
	data.append("file", file)
	data.append("business", business)
	data.append("contract", contract)

	dispatch(setLoader(true))

	let response: any
	try {
		response = await API.put({
			url: Endpoints.uriOrders("import-boxes"),
			data,
			axiosConfig: {
				timeout: 0,
			},
		})

		if (response.ok) {
			handleShowResponse({
				successCount: response.data.results?.successCount || 0,
				failedObjects: response.data.results?.invalidOrders || [],
			})
		} else {
			notification.error({
				message: Strings.sidebar.orders,
				description: response?.data?.message || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}
	} catch (err) {
		notification.error({
			message: Strings.sidebar.orders,
			description: response?.data?.message || Strings.serverErrors.wentWrong,
			placement: "bottomRight",
			duration: 5,
		})
	} finally {
		dispatch(setLoader(false))
	}
}

function downloadTemplate(name: string, sheetName: string, fields: string[]) {
	// Create a simple Excel template with the required columns for box import
	const worksheetData = [fields]
	// XLSX library required: npm install xlsx
	// Dynamically import XLSX to avoid bundling issues
	import("xlsx").then((XLSX) => {
		const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)
		const workbook = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(workbook, worksheet, sheetName || "Sheet1")
		const excelBuffer = XLSX.write(workbook, {
			bookType: "xlsx",
			type: "array",
		})
		const blob = new Blob([excelBuffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		})
		const link = document.createElement("a")
		const url = URL.createObjectURL(blob)
		link.href = url
		link.download = `${name || "template"}.xlsx`
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
		URL.revokeObjectURL(url)
	})
}

export default ImportModal
